import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import GDPRConsent from './components/GDPRConsent';
import Clarity from '@microsoft/clarity';
import useAdminAuth from './hooks/useAdminAuth';
import YektanetScript from './components/YektanetScript';
import ProtectedRoute from './ProtectedRoute';
import ProtectedAdminRoute from './ProtectedAdminRoute';

// General Pages
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import LandHezarPage from './pages/LandHezarPage';
import Onboarding from './pages/Onboarding';
import HomePage from './pages/HomePage';
import Profile from './pages/Profile';
import Scan from './pages/Scan';
import ScanPage from './pages/ScanPage';
import SkinAnalysisPage from './pages/SkinAnalysisPage';
import Routine from './pages/Routine';
import RoutineRequestCheck from './pages/RoutineRequestCheck';
import ProfitScanPage from './pages/ProfitScanPage';
import TestKYC from './pages/testkyc';
import ProfilePage from './pages/ProfilePage';
import Privacy from './pages/Privacy';
import SettingsPage from './pages/SettingsPage';
import TestRoutine from './TestRoutine';
import SetAdmin from './pages/SetAdmin';
import PhoneLoginTest from './pages/PhoneLoginTest';

// Layouts
import MainLayout from './components/MainLayout';

// Admin Pages - Lazy Load
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = React.lazy(() => import('./pages/admin/AdminOverview'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminAnalyses = React.lazy(() => import('./pages/admin/AdminAnalyses'));
const AdminRequests = React.lazy(() => import('./pages/admin/AdminRequests'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminRoutines = React.lazy(() => import('./pages/admin/AdminRoutines'));
const AdminProductAnalyses = React.lazy(() => import('./pages/admin/AdminProductAnalyses'));
// Lazy load blog components
const AdminBlogPage = React.lazy(() => import('./pages/admin/AdminBlogPage'));
const AdminBlogPostsList = React.lazy(() => import('./pages/admin/blog/AdminBlogPostsList'));
const AdminCreateBlogPost = React.lazy(() => import('./pages/admin/blog/AdminCreateBlogPost'));
const AdminEditBlogPost = React.lazy(() => import('./pages/admin/blog/AdminEditBlogPost'));

// Clarity project ID from environment
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="animate-spin h-12 w-12 border-4 border-black border-t-transparent rounded-full"></div>
  </div>
);

// Function to apply theme class to HTML element (can be moved to a utils file)
const applyTheme = (theme) => {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
};

const AppRoutes = () => {
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    applyTheme(savedTheme);
    if (process.env.NODE_ENV === 'production' && CLARITY_ID) {
      Clarity.init(CLARITY_ID);
    }
  }, []);

  const showDebugConsole = false;
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoggedIn = Boolean(localStorage.getItem('firebaseIdToken'));

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* --- Public Routes --- */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landcamp-summer" element={<LandHezarPage />} />
          <Route path="/login" element={
            <>
              <GDPRConsent clarityId={CLARITY_ID}><Login /></GDPRConsent>
              {/* Temporarily comment out YektanetScript on login page */}
              {/* <YektanetScript /> */}
            </>
          } />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/testkyc" element={<TestKYC />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/set-admin" element={<SetAdmin />} />
          <Route path="/phone-login-test" element={<PhoneLoginTest />} />

          {/* --- Protected User Routes --- */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/scanpage" element={<Scan />} />
              <Route path="/analysis" element={<SkinAnalysisPage />} />
              <Route path="/analysis/:analysisId" element={<SkinAnalysisPage />} />
              {/* Redirect old routes to new ones */}
              <Route path="/skin-analysis" element={<Navigate to="/analysis" replace />} />
              <Route path="/skin-analysis/:analysisId" element={<Navigate to="/analysis/:analysisId" replace />} />
              <Route path="/routine" element={<Routine />} />
              <Route path="/routine/check" element={<RoutineRequestCheck />} />
              <Route path="/profitscan" element={<ProfitScanPage />} />
              <Route path="/test-routine" element={<TestRoutine />} />
            </Route>
          </Route>

          {/* --- Protected Admin Routes --- */}
          <Route element={<ProtectedAdminRoute />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="analyses" element={<AdminAnalyses />} />
              <Route path="requests" element={<AdminRequests />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="routines" element={<AdminRoutines />} />
              <Route path="product-analyses" element={<AdminProductAnalyses />} />
              <Route path="blog" element={<AdminBlogPage />}>
                <Route index element={<Navigate to="posts" replace />} />
                <Route path="posts" element={<AdminBlogPostsList />} />
                <Route path="create" element={<AdminCreateBlogPost />} />
                <Route path="edit/:postId" element={<AdminEditBlogPost />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Suspense>
      {location.pathname !== '/login' && isLoggedIn && !isAdminRoute && <YektanetScript />}
    </>
  );
};

export default AppRoutes; 